// src/components/layout/NavBar.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import ThemeToggle from "../ui/ThemeToggle";
import axiosInstance from "../../utils/AxiosInstance";
import { clearAuth } from "../../utils/authStorage";
import { useScratchFeature } from "../../contexts/ScratchFeatureContext";

/**
 * Top navigation.
 *
 * Logged-out: Home · How it Works · Plans · FAQ · Contact · [toggle] · [Sign up]
 * Logged-in:  Generator · Plans · Settings · [toggle] · [Admin]? · [Dashboard]
 *
 * The [Admin] button only renders when the logged-in user has
 * `type === "ADMIN"`. It uses a ghost button with a shield-check icon to
 * make admin-mode visually distinct from regular CTAs without overpowering
 * the primary Dashboard action.
 */
export default function NavBar({ onScrollToSection }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { scratchEnabled } = useScratchFeature();

  const [menuOpen, setMenuOpen]         = useState(false);
  const [isLoggedIn, setIsLoggedIn]     = useState(false);
  const [isAdmin, setIsAdmin]           = useState(false);
  const [authLoading, setAuthLoading]   = useState(true);
  const authCheckInFlight = useRef(false);

  const checkUser = useCallback(async () => {
    if (authCheckInFlight.current) return;
    authCheckInFlight.current = true;
    setAuthLoading(true);
    try {
      const res  = await axiosInstance.get("/site/user/logged-user");
      const user = res?.data;
      setIsLoggedIn(Boolean(user?.id));
      setIsAdmin(user?.type === "ADMIN");
    } catch (err) {
      if (err?.response?.status === 401) clearAuth();
      setIsLoggedIn(false);
      setIsAdmin(false);
    } finally {
      authCheckInFlight.current = false;
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
    const onAuthChanged = (e) => {
      const authenticated = Boolean(e.detail?.authenticated);
      setIsLoggedIn(authenticated);
      if (!authenticated) {
        setIsAdmin(false);
        setAuthLoading(false);
        return;
      }
      // If the event payload carries the user, we can avoid an extra
      // round-trip. AdminRoute fires this with `user` in detail.
      const evtUser = e.detail?.user;
      if (evtUser) {
        setIsAdmin(evtUser.type === "ADMIN");
        setAuthLoading(false);
      } else {
        // Otherwise re-fetch to determine type.
        checkUser();
      }
    };
    window.addEventListener("auth:changed", onAuthChanged);
    return () => window.removeEventListener("auth:changed", onAuthChanged);
  }, [checkUser]);

  const goSection = (id) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    if (typeof onScrollToSection === "function") {
      onScrollToSection(id);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goRoute = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const publicLinks = [
    { id: "home",    label: "Home",         onClick: () => goRoute("/") },
    { id: "how",     label: "How it Works", onClick: () => goSection("how") },
    { id: "plans",   label: "Plans",        onClick: () => goRoute("/plans") },
    { id: "faq",     label: "FAQ",          onClick: () => goSection("faq") },
    { id: "contact", label: "Contact",      onClick: () => goSection("contact") },
  ];

  // Logged-in users: NO Dashboard link here — it's the CTA on the right.
  // Settings link gives users one-tap access to password / subscription / support.
  const privateLinks = [
    { id: "gen",      label: "Generator",  onClick: () => goRoute("/generator") },
    ...(scratchEnabled
      ? [{ id: "scratch", label: "Scratch-Off", onClick: () => goRoute("/scratch") }]
      : []),
    { id: "plans",    label: "Plans",      onClick: () => goRoute("/plans")     },
    { id: "settings", label: "Settings",   onClick: () => goRoute("/settings")  },
  ];

  const links = isLoggedIn ? privateLinks : publicLinks;

  const linkClass =
    "px-2 py-1.5 text-[14px] font-medium text-[var(--txt)] hover:text-[var(--gold)] transition-colors";

  const showAdmin = !authLoading && isLoggedIn && isAdmin;

  return (
    <header
      className="fixed inset-x-0 top-0 z-50"
      style={{
        background: "color-mix(in srgb, var(--bg) 88%, transparent)",
        borderBottom: "1px solid var(--border)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      <div className="mx-auto max-w-[1180px] flex items-center justify-between h-14 px-4 sm:px-6">
        <Logo onClick={() => goRoute("/")} />

        <nav className="hidden md:flex items-center gap-5">
          {links.map((l) => (
            <button key={l.id} type="button" onClick={l.onClick} className={linkClass}>
              {l.label}
            </button>
          ))}

          {!authLoading && !isLoggedIn && (
            <button type="button" onClick={() => goRoute("/login")} className={linkClass}>
              Log in
            </button>
          )}

          <ThemeToggle />

          {!authLoading && !isLoggedIn && (
            <Button size="sm" onClick={() => goRoute("/sign-up")}>
              Sign up
            </Button>
          )}

          {showAdmin && (
            <Button
              size="sm"
              variant="ghost"
              icon="shield-check"
              onClick={() => goRoute("/admin")}
              title="Admin console"
            >
              Admin
            </Button>
          )}

          {!authLoading && isLoggedIn && (
            <Button size="sm" onClick={() => goRoute("/dashboard")}>
              Dashboard
            </Button>
          )}
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle size={32} />
          <button
            type="button"
            className="p-2 -mr-2 text-[var(--txt)]"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            <Icon name={menuOpen ? "x" : "menu-2"} size={22} />
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-300 ${
          menuOpen ? "max-h-[460px]" : "max-h-0"
        }`}
        style={{
          borderTop: menuOpen ? "1px solid var(--border)" : "none",
          background: "color-mix(in srgb, var(--bg) 96%, transparent)",
        }}
      >
        <nav className="flex flex-col px-4 py-3 gap-1">
          {links.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={l.onClick}
              className="w-full text-left px-3 py-2.5 rounded-lg text-[14px] font-medium text-[var(--txt)] hover:bg-[var(--surf-2)] transition-colors"
            >
              {l.label}
            </button>
          ))}

          {showAdmin && (
            <button
              type="button"
              onClick={() => goRoute("/admin")}
              className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg text-[14px] font-medium text-[var(--txt)] hover:bg-[var(--surf-2)] transition-colors"
            >
              <Icon name="shield-check" size={16} style={{ color: "var(--gold)" }} />
              Admin console
            </button>
          )}

          {!authLoading && !isLoggedIn && (
            <>
              <button
                onClick={() => goRoute("/login")}
                className="w-full text-left px-3 py-2.5 rounded-lg text-[14px] font-medium text-[var(--txt)] hover:bg-[var(--surf-2)] transition-colors"
              >
                Log in
              </button>
              <div className="px-1 pt-2">
                <Button full onClick={() => goRoute("/sign-up")}>
                  Sign up — free
                </Button>
              </div>
            </>
          )}

          {!authLoading && isLoggedIn && (
            <div className="px-1 pt-2">
              <Button full onClick={() => goRoute("/dashboard")}>
                Dashboard
              </Button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}
